import { isApiError, unwrap, type components } from "@sagot/api-client";
import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { declaredOutputs, paramSpecs, type ParamSpec } from "./connector-op-model";

type Schemas = components["schemas"];

/** A connector as the picker lists it. */
export type PickerConnector = Pick<Schemas["ConnectorSummaryOut"], "id" | "name" | "status" | "operations" | "active_environment" | "sandbox">;
/** An operation as the picker lists it. */
export type PickerOperation = Pick<Schemas["OperationSummaryOut"], "key" | "name" | "kind" | "published_version" | "ai_description">;
/** What a "Test" sends and gets back (the test console of the Connectors platform). */
export type OperationTestIn = Schemas["ConnectorOperationTestIn"];
export type OperationTestOut = Schemas["ConnectorOperationTestOut"];

const STALE_MS = 30_000;

const keys = {
  connectors: (ws: string) => ["ws", ws, "connectors", "picker"] as const,
  operations: (ws: string, connectorId: string) => ["ws", ws, "connectors", connectorId, "operations", "picker"] as const,
  operation: (ws: string, connectorId: string, key: string) => ["ws", ws, "connectors", connectorId, "operations", key, "signature"] as const,
};

/** The connectors the picker can list: seeing them needs `connectors.view`. */
export function useConnectorsPicker(): { query: UseQueryResult<PickerConnector[]>; readable: boolean } {
  const api = useApi();
  const ws = useWorkspaceId();
  const permissions = usePermissions();
  const readable = permissions.can("connectors.view");
  const query = useQuery({
    queryKey: keys.connectors(ws),
    queryFn: async () => (await unwrap(api.GET("/v1/connectors"))).items,
    enabled: readable,
    retry: false,
    staleTime: STALE_MS,
  });
  return { query, readable: readable || permissions.isPending };
}

/** The operations of one connector. */
export function useOperationsPicker(connectorId: string): UseQueryResult<PickerOperation[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: keys.operations(ws, connectorId),
    queryFn: () => unwrap(api.GET("/v1/connectors/{connector_id}/operations", { params: { path: { connector_id: connectorId } } })),
    enabled: connectorId !== "",
    retry: false,
    staleTime: STALE_MS,
  });
}

/** What an operation takes and gives, read from its published version (or its draft when nothing is published). */
export interface OperationSignature {
  key: string;
  name: string;
  kind: string;
  aiDescription: string;
  publishedVersion: number | null;
  params: ParamSpec[];
  outputs: string[];
}

export function useOperationSignature(connectorId: string, key: string): UseQueryResult<OperationSignature> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: keys.operation(ws, connectorId, key),
    queryFn: async (): Promise<OperationSignature> => {
      const found = await unwrap(api.GET("/v1/connectors/{connector_id}/operations/{key}", { params: { path: { connector_id: connectorId, key } } }));
      const version = found.published ?? found.draft;
      return {
        key: found.key,
        name: found.name,
        kind: found.kind,
        aiDescription: found.ai_description,
        publishedVersion: found.published_version,
        params: paramSpecs(version?.params_schema),
        outputs: declaredOutputs(version?.response_mapping),
      };
    },
    enabled: connectorId !== "" && key !== "",
    retry: false,
    staleTime: STALE_MS,
  });
}

/** Why a test did not come back (the operation may still have failed: that is an answer, `ok: false`). */
export type OperationTestFailure = "rate_limited" | "not_found" | "forbidden" | "plan" | "network" | "unknown";

export function operationTestFailureOf(error: unknown): OperationTestFailure {
  if (!isApiError(error)) return "unknown";
  if (error.kind === "network" || error.kind === "timeout") return "network";
  switch (error.status) {
    case 429:
      return "rate_limited";
    case 404:
      return "not_found";
    case 403:
      return "forbidden";
    case 402:
      return "plan";
    default:
      return "unknown";
  }
}

/** Sends the operation once through the test console, with sample parameters; the request and answer come back masked. */
export function useOperationTest(connectorId: string, key: string): UseMutationResult<OperationTestOut, unknown, OperationTestIn> {
  const api = useApi();
  return useMutation({
    mutationFn: (body: OperationTestIn) => unwrap(api.POST("/v1/connectors/{connector_id}/operations/{key}/test", { params: { path: { connector_id: connectorId, key } }, body })),
  });
}
